import React from "react";
import { Shield, Phone, ExternalLink, AlertTriangle, Heart } from "lucide-react";
import SEO from "@/components/common/SEO";
import { SITE_META } from "@/data/staticContent";

const RESOURCES = [
    {
        title: "SinAdicciones.org",
        desc: "Comunidad y archivo de recuperación fundada por @NitanAdicto. Información, contactos y experiencias compartidas.",
        url: SITE_META.socials.sinAdicciones,
        cta: "Visitar sitio",
        featured: true,
    },
    {
        title: "SENDA Chile",
        desc: "Servicio Nacional para la Prevención y Rehabilitación del Consumo de Drogas y Alcohol. Orientación gratuita.",
        url: "https://www.senda.gob.cl",
        cta: "Ir a SENDA",
    },
    {
        title: "Fonodrogas 1412",
        desc: "Línea telefónica gratuita y confidencial. Atención 24/7 en todo Chile.",
        url: "tel:1412",
        cta: "Llamar al 1412",
    },
    {
        title: "Salud Responde 600 360 7777",
        desc: "Línea de salud mental del MINSAL. Orientación profesional, derivación a urgencias.",
        url: "tel:6003607777",
        cta: "Llamar",
    },
];

export default function Salida() {
    return (
        <>
            <SEO
                title="Salida de emergencia · ¿Te reflejó demasiado?"
                description="Si esto dejó de parecer chiste, quizás ya no es contenido: es una señal. Aquí están los recursos reales para pedir ayuda."
                path="/salida"
                keywords="ayuda adicción Chile, SENDA, Fonodrogas 1412, SinAdicciones, rehabilitación, urgencia"
            />

            <section className="bg-[var(--warning)] text-black border-b-4 border-black">
                <div className="hazard-stripes h-2 w-full" />
                <div className="container-x py-20 md:py-28">
                    <div className="flex items-start gap-6 mb-8">
                        <Shield className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0" />
                        <div>
                            <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.4em] mb-4">
                                Salida de emergencia · Sección 13
                            </p>
                            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tight">
                                ¿Te reflejó
                                <br />
                                demasiado?
                            </h1>
                        </div>
                    </div>
                    <p className="font-mono text-base md:text-xl leading-relaxed max-w-3xl">
                        Si esto dejó de parecer chiste, quizás ya no es
                        contenido: es una señal. No tienes que tocar otro fondo
                        para pedir ayuda.
                    </p>
                </div>
                <div className="hazard-stripes h-2 w-full" />
            </section>

            <section className="py-16 md:py-24" data-testid="salida-resources">
                <div className="container-x">
                    <p className="eyebrow mb-6">Recursos reales</p>
                    <h2 className="font-display text-4xl md:text-5xl mb-12 leading-[0.95]">
                        Aquí no hay sátira. Aquí hay teléfonos y enlaces.
                    </h2>

                    <div className="grid md:grid-cols-2 gap-6">
                        {RESOURCES.map((r, i) => (
                            <a
                                key={r.title}
                                href={r.url}
                                target={r.url.startsWith("http") ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                className={`block dossier hover:border-[var(--danger)] transition-colors ${r.featured ? "md:col-span-2 border-[var(--danger)]" : ""}`}
                                data-testid={`resource-${i}`}
                            >
                                <div className="dossier-header">
                                    <span>
                                        {r.url.startsWith("tel:") ? (
                                            <>
                                                <Phone className="inline w-3 h-3 mr-2" />
                                                Línea telefónica
                                            </>
                                        ) : (
                                            <>
                                                <ExternalLink className="inline w-3 h-3 mr-2" />
                                                Enlace externo
                                            </>
                                        )}
                                    </span>
                                    {r.featured && (
                                        <span className="text-[var(--danger)]">
                                            Recomendado
                                        </span>
                                    )}
                                </div>
                                <div className="dossier-content">
                                    <h3 className="font-display text-2xl md:text-3xl text-[var(--paper-2)] mb-3">
                                        {r.title}
                                    </h3>
                                    <p className="font-mono text-sm text-[var(--paper)] leading-relaxed mb-6">
                                        {r.desc}
                                    </p>
                                    <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--warning)]">
                                        {r.cta}
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </span>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 md:py-20 bg-[#0d0d0d] border-y border-[var(--line)]">
                <div className="container-x max-w-3xl text-center">
                    <Heart className="w-12 h-12 mx-auto mb-6 text-[var(--danger)]" />
                    <p className="font-hand text-3xl md:text-4xl text-[var(--warning)] -rotate-1 inline-block mb-6">
                        Si estás aquí, ya es un paso.
                    </p>
                    <p className="font-mono text-base md:text-lg text-[var(--paper)] leading-relaxed mb-4">
                        No hay vergüenza en pedir ayuda. Hay vergüenza en seguir
                        pretendiendo que no la necesitas.
                    </p>
                    <p className="font-mono text-sm text-[var(--ink-muted)]">
                        Si estás en riesgo, no puedes parar o tienes ideas de
                        hacerte daño, llama al{" "}
                        <a
                            href="tel:600360777"
                            className="text-[var(--danger)] underline font-bold"
                        >
                            600 360 7777
                        </a>{" "}
                        o acude a un servicio de urgencia.
                    </p>
                </div>
            </section>

            <section className="py-16 grain">
                <div className="container-x max-w-3xl">
                    <div className="border-2 border-[var(--danger)] p-8 md:p-12">
                        <p className="eyebrow mb-4">Advertencia responsable</p>
                        <div className="flex items-start gap-4">
                            <AlertTriangle className="w-6 h-6 text-[var(--danger)] flex-shrink-0 mt-1" />
                            <p className="font-mono text-sm text-[var(--paper)] leading-relaxed">
                                Este sitio contiene textos personales, irónicos
                                y testimoniales sobre adicción, consumo, recaídas
                                y recuperación. No reemplaza evaluación médica,
                                terapia psicológica, psiquiatría, tratamiento
                                profesional ni ayuda de emergencia. Si estás en
                                riesgo, busca ayuda profesional inmediata.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
